import { UseMutationResult } from "@tanstack/react-query";

import { checkoutRedirect } from "@/lib/checkout-redirect";
import { CheckoutResponse, PaymentMethodData } from "@/networking/checkout.api";

export function useHandleCheckout(
  cartToken: string,
  checkoutMutation: UseMutationResult<
    CheckoutResponse,
    unknown,
    {
      cartToken: string;
      shouldIncludeBrowserInfo: boolean;
      kountSessionId?: string;
      paymentMethodData?: PaymentMethodData;
      deviceData?: string;
      acceptedAgreementIds?: number[];
    }
  >,
  setLoading: (b: boolean) => void,
  setVgsThreeDSModal?: (modal: {
    isOpen: boolean;
    challengeHtml: string;
    challengeUrl: string;
    transactionId: string;
    merchantTxId: string;
  }) => void,
  kountSessionId?: string,
  getPaymentMethodData?: () => PaymentMethodData | undefined,
  setBlikOtpModal?: (modal: { isOpen: boolean }) => void,
  deviceData?: string,
  acceptedAgreementIds?: number[],
) {
  return async function handleCheckout() {
    try {
      setLoading(true);

      const paymentMethodData = getPaymentMethodData?.();

      const res = await checkoutMutation.mutateAsync({
        cartToken,
        shouldIncludeBrowserInfo: true,
        kountSessionId,
        paymentMethodData,
        deviceData,
        acceptedAgreementIds,
      });

      const provider = res.three_ds_provider;

      // Check if 3DS is required
      if (provider === "vgs") {
        if (!setVgsThreeDSModal) {
          throw new Error("VGS 3DS modal handler not configured");
        }

        if (!res.challenge_form) {
          throw new Error(
            "VGS 3DS authentication failed: Missing challenge HTML. Please try again or contact support.",
          );
        }

        if (!res.transaction_id) {
          throw new Error(
            "VGS 3DS authentication failed: Missing transaction ID. Please try again or contact support.",
          );
        }

        setVgsThreeDSModal({
          isOpen: true,
          challengeHtml: res.challenge_form,
          challengeUrl: res.challenge_url ?? "",
          transactionId: res.transaction_id,
          merchantTxId: res.merchant_tx_id ?? "",
        });

        setLoading(false);
        return;
      }

      // Check if BLIK OTP is needed (recurring BLIK requires OTP before agreement creation)
      if (res.blik_agreement_pending) {
        setBlikOtpModal?.({ isOpen: true });
        setLoading(false);
        return;
      }

      checkoutRedirect(res, cartToken);
    } catch (error) {
      console.error("Checkout error:", error);
      setLoading(false);
      throw error;
    }
  };
}
